<template>
  <div role="main" :aria-label="t('dashboard.title')">
    <!-- Enhanced Page Header with Simplified API -->
    <PageHeader
      :title="t('dashboard.title')"
      :icon-path="ICONS.dashboard"
      :welcome-user="userProfile?.name"
      :helper-text-mobile="t('dashboard.helperTextMobile')"
      :helper-text-desktop="t('dashboard.helperTextDesktop')"
      :action-buttons="headerActions"
    >
      <template #center>
        <!-- Enhanced Search Bar with Simplified API -->
        <SearchBar
          v-model="searchQuery"
          :results="searchResults"
          :placeholder="isMobile ? t('dashboard.searchPlaceholder') : t('dashboard.searchPlaceholderFull')"
          :filter-options="SEARCH_TYPE_OPTIONS"
          :show-filter="true"
          :filter-label="t('dashboard.filterSearchResults')"
          :dropdown-title="t('dashboard.searchResultsTitle')"
          :empty-message="t('dashboard.noSearchResultsFor') + ' '"
          :header-badge-config="searchHeaderBadge"
          :empty-icon="ICONS.search"
          :wrapper-class="'w-full'"
          :result-component="SearchResultItem"
          @search="handleSearch"
          @clear="clearSearch"
          @filter-change="handleFilterChange"
          @result-click="navigateToResult"
        />
      </template>
    </PageHeader>

    <!-- Enhanced Stats Cards -->
    <section :aria-label="t('aria.dashboardStatistics')" role="region" class="px-4 md:px-6 py-4">
      <StatsGrid :stats="dashboardStats" :loading="statsLoading" />
    </section>

    <!-- Teams & Projects Panel -->
    <div class="px-4 md:px-6 pb-4">
      <TeamsProjectsPanel
        ref="teamsProjectsPanelRef"
        :teams="filteredTeams"
        :loading="loading"
        :stats-loading="statsLoading"
        v-model:selected-team-id="selectedTeamId"
        :can-manage="can.createTeams() || can.createProjects()"
        @manage-teams="goToTeamsPage"
        @create-project="goToTeamPage"
      />
    </div>
    
    <!-- Project Selector Modal -->
    <ProjectSelectorModal
      :is-open="showProjectSelector"
      title="Select a Project"
      description="Choose a project to report a bug in"
      :projects="availableProjects"
      @confirm="handleProjectSelected"
      @cancel="showProjectSelector = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import StatsGrid from '@/components/ui/StatsGrid.vue'
import SearchBar from '@/components/ui/SearchBar.vue'
import SearchResultItem from '@/components/dashboard/SearchResultItem.vue'
import TeamsProjectsPanel from '@/components/dashboard/TeamsProjectsPanel.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import ProjectSelectorModal from '@/components/ui/ProjectSelectorModal.vue'
// Database services
import { teamService, projectService, userService, testCaseService, bugService, featureService, testRunService } from '@/database/services'
import { useToast } from '@/composables/useToast'
// Constants and Utilities
import { 
  SEARCH_TYPES, 
  SEARCH_TYPE_OPTIONS, 
  SEARCH_TYPE_LABELS,
  STAT_CARD_ICONS,
  STAT_CARD_COLORS,
  EMPTY_STATE_MESSAGES
} from '@/constants/dashboard'
import { ICONS } from '@/constants/icons'
import { STATUS_VALUES, USER_ROLES, VISIBILITY_OPTIONS } from '@/constants/common'
import { FEATURE_STATUS } from '@/constants/featureConstants'
import { 
  getSearchTypeBadge,
} from '@/utils/badgeHelpers'
import { t, tPlural } from '@/i18n/en-us'

// Stores
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const { can } = usePermissions()

// Reactive data
const loading = ref(false)
const statsLoading = ref(true) // Separate loading state for stats
const selectedTeamId = ref(null) // For two-column layout
const teams = ref([])
const searchQuery = ref('')
const searchType = ref(SEARCH_TYPES.ALL) // Filter search results by type
const filteredTeams = ref([])
const searchResults = ref([])
const teamsProjectsPanelRef = ref(null)
const isMobile = ref(window.innerWidth < 768)
const showProjectSelector = ref(false)

// Cache for loaded data to avoid redundant loading
const dataCache = ref({
  testCases: null,
  bugs: null,
  features: null,
  testRuns: null,
  users: null,
  lastLoaded: null
})

// Debounce timer for search
let searchDebounceTimer = null

// Computed
const userProfile = computed(() => authStore.userProfile)

// Get available projects for bug reporting
const availableProjects = computed(() => {
  const projects = []
  teams.value.forEach(team => {
    team.projects?.forEach(project => {
      projects.push({
        id: project.id,
        name: project.name || project.title
      })
    })
  })
  return projects
})

// Header action buttons (declarative API)
const headerActions = computed(() => [
  {
    label: 'My Tasks',
    mobileLabel: 'My Tasks',
    icon: ICONS.user,
    onClick: goToAssignedToMe,
    variant: 'outline',
    size: 'md'
  },
  {
    label: 'Cross Team Sanity',
    mobileLabel: 'Sanity',
    icon: ICONS.team,
    onClick: goToCrossTeamSanity,
    variant: 'outline',
    size: 'md',
    color: 'blue'
  },
  {
    label: 'Report Bug',
    mobileLabel: 'Report',
    icon: ICONS.bug,
    onClick: reportBug,
    variant: 'outline',
    size: 'md',
    color: 'red'
  },
  {
    label: t('dashboard.viewReports'),
    mobileLabel: t('dashboard.viewReportsShort'),
    icon: ICONS.report,
    onClick: goToReports,
    variant: 'primary',
    size: 'md'
  }
])

// Search header badge config (declarative API)
const searchHeaderBadge = computed(() => {
  if (searchType.value && searchType.value !== SEARCH_TYPES.ALL) {
    return {
      label: SEARCH_TYPE_LABELS[searchType.value],
      variant: getSearchTypeBadge(searchType.value).variant
    }
  }
  return null
})

// Optimized stats computation with reduced nested loops
const stats = computed(() => {
  const totalTeams = teams.value.length
  let totalProjects = 0
  let activeTestCases = 0
  let activeBugs = 0
  let activeParentFeatures = 0
  let totalTestCasesCount = 0
  const allStatuses = []
  
  // Single pass through teams and projects for better performance
  for (const team of teams.value) {
    const projects = team.projects || []
    totalProjects += projects.length
    
    for (const project of projects) {
      // Count active test cases
      const testCases = project.testCasesData || []
      totalTestCasesCount += testCases.length
      
      // Collect all statuses for debugging
      testCases.forEach(tc => {
        if (tc.status) {
          allStatuses.push(tc.status)
        }
      })
      
      const activeInProject = testCases.filter(tc => 
        tc.status?.toLowerCase() === STATUS_VALUES.TEST_CASE.ACTIVE
      ).length
      activeTestCases += activeInProject
      
      // Count active bugs (not closed)
      const bugs = project.bugsData || []
      activeBugs += bugs.filter(bug => 
        bug.status?.toLowerCase() !== STATUS_VALUES.BUG.CLOSED
      ).length
      
      // Count parent features (not closed)
      const features = project.featuresData || []
      activeParentFeatures += features.filter(feature => {
        const hasNoParent = !feature.parent_id
        const isNotClosed = feature.status?.toLowerCase() !== FEATURE_STATUS.CLOSED
        return hasNoParent && isNotClosed
      }).length
    }
  }
  
  return {
    teams: totalTeams,
    projects: totalProjects,
    features: activeParentFeatures,
    testCases: activeTestCases,
    bugs: activeBugs
  }
})

// StatsGrid data
const dashboardStats = computed(() => [
  {
    label: t('common.teams'),
    value: stats.value.teams,
    subtext: t('dashboard.activeTeams'),
    iconPath: STAT_CARD_ICONS.TEAMS,
    colors: STAT_CARD_COLORS.TEAMS
  },
  {
    label: t('common.projects'),
    value: stats.value.projects,
    subtext: t('dashboard.totalProjects'),
    iconPath: STAT_CARD_ICONS.PROJECTS,
    colors: STAT_CARD_COLORS.PROJECTS
  },
  {
    label: t('common.features'),
    value: stats.value.features,
    subtext: t('dashboard.openParentFeatures'),
    iconPath: STAT_CARD_ICONS.FEATURES,
    colors: STAT_CARD_COLORS.FEATURES
  },
  {
    label: t('common.testCases'),
    value: stats.value.testCases,
    subtext: t('dashboard.activeTestCases'),
    iconPath: STAT_CARD_ICONS.TEST_CASES,
    colors: STAT_CARD_COLORS.TEST_CASES
  },
  {
    label: t('common.bugs'),
    value: stats.value.bugs,
    subtext: t('dashboard.openBugs'),
    iconPath: STAT_CARD_ICONS.BUGS,
    colors: STAT_CARD_COLORS.BUGS
  }
])


// Helper function to check if element is visible in viewport
const isElementVisible = (element) => {
  if (!element) return false
  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight || document.documentElement.clientHeight
  // Check if the element's top is visible in the viewport (with some buffer)
  return rect.top >= 0 && rect.top <= windowHeight
}

// Watch for selectedTeamId changes to scroll to Teams & Projects section
watch(selectedTeamId, async (newTeamId) => {
  // Scroll whenever a team is selected
  if (newTeamId) {
    await nextTick()
    // Only scroll if the Teams & Projects header is not currently visible
    const panelSection = teamsProjectsPanelRef.value?.teamsProjectsSection
    if (panelSection && !isElementVisible(panelSection)) {
      panelSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
})

// Watch for search query changes (debounced for performance)
watch(searchQuery, (newQuery) => {
  // Clear any existing debounce timer
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
  }
  
  // If search is empty, update immediately
  if (!newQuery.trim()) {
    filteredTeams.value = teams.value
    return
  }
  
  // Debounce the search to avoid excessive filtering
  searchDebounceTimer = setTimeout(() => {
    const query = newQuery.toLowerCase().trim()
    
    // Filter teams and projects based on search query
    const filtered = teams.value.map(team => {
      // Check if team name matches
      const teamMatches = team.name.toLowerCase().includes(query)
      
      // Filter projects within this team
      const filteredProjects = team.projects?.filter(project => {
        return projectMatchesSearch(project, query)
      }) || []
      
      // If team name matches, show all projects in that team
      if (teamMatches) {
        return {
          ...team,
          projects: team.projects || [] // Show all projects when team matches
        }
      }
      
      // If team doesn't match but has matching projects, show only matching projects
      if (filteredProjects.length > 0) {
        return {
          ...team,
          projects: filteredProjects
        }
      }
      
      return null
    }).filter(team => team !== null)
    
    // If no matches found, show all teams and projects
    filteredTeams.value = filtered.length > 0 ? filtered : teams.value
  }, 300) // 300ms debounce delay
}, { immediate: true })

// Watch for teams changes to update filtered teams (optimized)
watch(teams, (newTeams) => {
  if (!searchQuery.value.trim()) {
    filteredTeams.value = newTeams
    return
  }
  
  // Apply current search filter to new teams data
  const query = searchQuery.value.toLowerCase().trim()
  
  const filtered = newTeams.map(team => {
    const teamMatches = team.name.toLowerCase().includes(query)
    
    // If team name matches, show all projects
    if (teamMatches) {
      return {
        ...team,
        projects: team.projects || []
      }
    }
    
    // Otherwise filter projects
    const filteredProjects = team.projects?.filter(project => {
      return projectMatchesSearch(project, query)
    }) || []
    
    // If team doesn't match but has matching projects, show only matching projects
    if (filteredProjects.length > 0) {
      return {
        ...team,
        projects: filteredProjects
      }
    }
    
    return null
  }).filter(team => team !== null)
  
  // If no matches found, show all teams and projects
  filteredTeams.value = filtered.length > 0 ? filtered : newTeams
}, { immediate: true })

// Methods
// OPTIMIZATION: Use sessionStorage to persist cache across page refreshes
const CACHE_KEY_PREFIX = 'dashboard_'
const CACHE_DURATION = 60000 // 60 seconds

// Helper functions to get/set cache
const getCache = (key) => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key)
    if (cached) {
      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()
      if ((now - timestamp) < CACHE_DURATION) {
        return data
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null
}

const setCache = (key, data) => {
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
}

const loadData = async (forceRefresh = false) => {
  // Guard: Don't load data if user is not authenticated
  if (!authStore.isAuthenticated || !authStore.user) {
    return
  }
  
  loading.value = true
  
  try {
    // Check if current user is Super Admin
    const isSuperAdmin = authStore.userProfile?.role?.toLowerCase() === USER_ROLES.SUPER_ADMIN
    const currentUserId = authStore.user?.uid
    
    // STEP 1: Load teams and projects FIRST (fast, lightweight) - this allows UI to show immediately
    let allTeams, allProjects
    if (!forceRefresh) {
      const cachedTeams = getCache('teams')
      const cachedProjects = getCache('projects')
      if (cachedTeams && cachedProjects) {
        allTeams = cachedTeams
        allProjects = cachedProjects
      } else {
        const results = await Promise.all([
      teamService.getAll(),
      projectService.getAll()
    ])
        allTeams = results[0]
        allProjects = results[1]
        
        // Cache the results
        setCache('teams', allTeams)
        setCache('projects', allProjects)
      }
    } else {
      const results = await Promise.all([
        teamService.getAll(),
        projectService.getAll()
      ])
      allTeams = results[0]
      allProjects = results[1]
      
      // Update cache
      setCache('teams', allTeams)
      setCache('projects', allProjects)
    }
    
    // Filter out deleted teams
    const activeTeams = allTeams.filter(team => !team.is_deleted)
    
    // Filter teams based on visibility
    const visibleTeams = activeTeams.filter(teamData => {
      // Super Admin can see all teams
      if (isSuperAdmin) return true
      
      // Private teams are visible to Super Admin and team members
      if (teamData.visibility === VISIBILITY_OPTIONS.PRIVATE) {
        return teamData.members && teamData.members.includes(currentUserId)
      }
      
      // Public teams are visible to everyone
      return true
    })
    
    // Filter out deleted projects
    const activeProjects = allProjects.filter(project => !project.is_deleted)
    
    // Filter projects based on visibility
    const visibleProjects = activeProjects.filter(projectData => {
      // Super Admin can see all projects
      if (isSuperAdmin) return true
      
      // Private projects are visible to Super Admin and project members
      if (projectData.visibility === VISIBILITY_OPTIONS.PRIVATE) {
        return projectData.members && projectData.members.includes(currentUserId)
      }
      
      // Public projects are visible to everyone
      return true
    })
    
    // OPTIMIZATION: Show UI immediately with teams/projects (empty stats), then load details in background
    // This makes the page feel much faster on first load
    // Initialize with empty teams data structure
    teams.value = visibleTeams.map(teamData => ({
      id: teamData.id,
      name: teamData.name || t('dashboard.unnamedTeam'),
      projects: visibleProjects
        .filter(project => project.teamId === teamData.id)
        .map(projectData => ({
          id: projectData.id,
          title: projectData.name || projectData.title || t('dashboard.untitledProject'),
          name: projectData.name || projectData.title || t('dashboard.untitledProject'),
          description: projectData.description || t('dashboard.noDescription'),
          status: projectData.status || STATUS_VALUES.PROJECT.ACTIVE,
          testCases: 0,
          bugs: 0,
          features: 0,
          testRuns: 0,
          testCasesData: [],
          bugsData: [],
          featuresData: [],
          testRunsData: [],
          createdBy: t('dashboard.unknown'),
          createdAt: new Date(projectData.created_at) || new Date(),
          updatedAt: new Date(projectData.updated_at) || new Date()
        }))
    }))
    
    loading.value = false
    // Keep statsLoading true until detailed data is loaded
    statsLoading.value = true
    
    // STEP 2: Load detailed data in background (non-blocking) - allows UI to render first
    // This function loads data asynchronously and updates the UI when ready
    const loadDetailedData = async () => {
      try {
    let allTestCases, allBugs, allFeatures, allTestRuns, allUsers
    
      if (!forceRefresh) {
        // Try to load from sessionStorage cache
        const cachedTestCases = getCache('testCases')
        const cachedBugs = getCache('bugs')
        const cachedFeatures = getCache('features')
        const cachedTestRuns = getCache('testRuns')
        const cachedUsers = getCache('users')
        
        if (cachedTestCases && cachedBugs && cachedFeatures && cachedTestRuns && cachedUsers) {
          // Ensure cached data is in array format
          const extractData = (item) => {
            if (Array.isArray(item)) {
              return item
            }
            if (item && typeof item === 'object' && 'data' in item) {
              return item.data || []
            }
            return []
          }
          
          allTestCases = extractData(cachedTestCases)
          allBugs = extractData(cachedBugs)
          allFeatures = extractData(cachedFeatures)
          allTestRuns = extractData(cachedTestRuns)
          allUsers = extractData(cachedUsers)
          
          // Also update in-memory cache
          dataCache.value = {
            testCases: allTestCases,
            bugs: allBugs,
            features: allFeatures,
            testRuns: allTestRuns,
            users: allUsers,
            lastLoaded: Date.now()
          }
          
          // Process and update UI immediately with cached data
          processAndUpdateUI(allTeams, allProjects, allTestCases, allBugs, allFeatures, allTestRuns, allUsers, isSuperAdmin, currentUserId, visibleTeams, visibleProjects)
          statsLoading.value = false
          return
        }
      }
      
      // OPTIMIZATION: Load data progressively - start with smaller chunks first
      // Load users first (usually smallest, needed for names)
      const userPromise = userService.getAll({ limit: null }).catch((err) => { console.error('Error loading users:', err); return [] })
      
      // Then load the rest in parallel
      const [usersResult, testCasesResult, bugsResult, featuresResult, testRunsResult] = await Promise.all([
        userPromise,
          testCaseService.getAll({ limit: null }).catch((err) => { console.error('Error loading test cases:', err); return [] }),
          bugService.getAll({ limit: null }).catch((err) => { console.error('Error loading bugs:', err); return [] }),
          featureService.getAll({ limit: null }).catch((err) => { console.error('Error loading features:', err); return [] }),
        testRunService.getAll({ limit: null }).catch((err) => { console.error('Error loading test runs:', err); return [] })
      ])
      
      // Handle both array and paginated response formats
      const extractData = (item) => {
        if (Array.isArray(item)) {
          return item
        }
        if (item && typeof item === 'object' && 'data' in item) {
          return item.data || []
        }
        return []
      }
      
      allUsers = extractData(usersResult)
      allTestCases = extractData(testCasesResult)
      allBugs = extractData(bugsResult)
      allFeatures = extractData(featuresResult)
      allTestRuns = extractData(testRunsResult)
      
      // Cache in both sessionStorage and memory
      setCache('testCases', allTestCases)
      setCache('bugs', allBugs)
      setCache('features', allFeatures)
      setCache('testRuns', allTestRuns)
      setCache('users', allUsers)
      
        dataCache.value = {
          testCases: allTestCases,
          bugs: allBugs,
          features: allFeatures,
          testRuns: allTestRuns,
          users: allUsers,
          lastLoaded: Date.now()
        }
      
      // Process and update UI with loaded data
      processAndUpdateUI(allTeams, allProjects, allTestCases, allBugs, allFeatures, allTestRuns, allUsers, isSuperAdmin, currentUserId, visibleTeams, visibleProjects)
        statsLoading.value = false
      } catch (error) {
        console.error('Error in loadDetailedData:', error)
        statsLoading.value = false
      }
      }
    
    // Helper function to process data and update UI
    const processAndUpdateUI = (allTeams, allProjects, allTestCases, allBugs, allFeatures, allTestRuns, allUsers, isSuperAdmin, currentUserId, visibleTeams, visibleProjects) => {
    // Filter out deleted items
    const activeTestCases = allTestCases.filter(tc => !tc.is_deleted)
    const activeBugs = allBugs.filter(bug => !bug.is_deleted)
    const activeFeatures = allFeatures.filter(feature => !feature.is_deleted)
    const activeTestRuns = allTestRuns.filter(run => !run.is_deleted)
    
    // Create user lookup map for quick access
    const userMap = new Map(allUsers.map(user => [user.id, user]))
    
    // Group projects by team
    const teamsData = []
    
      if (visibleTeams && visibleTeams.length > 0) {
      for (const teamData of visibleTeams) {
        const team = {
          id: teamData.id,
          name: teamData.name || t('dashboard.unnamedTeam'),
          projects: []
        }
        
        // Find projects for this team (only visible projects)
        const teamProjects = visibleProjects.filter(project => project.teamId === teamData.id)
        
        for (const projectData of teamProjects) {
          // Get user name from map
          let creatorName = t('dashboard.unknown')
          const creatorId = projectData.createdBy || projectData.created_by
          if (creatorId) {
            const user = userMap.get(creatorId)
            if (user) {
              creatorName = user.name || t('dashboard.unknown')
            }
          }
          
          // Filter data by project ID (no API calls, just array filtering)
          const testCasesData = activeTestCases.filter(tc => tc.projectId === projectData.id || tc.project_id === projectData.id)
          const bugsData = activeBugs.filter(bug => bug.projectId === projectData.id || bug.project_id === projectData.id)
          const featuresData = activeFeatures.filter(feature => feature.projectId === projectData.id || feature.project_id === projectData.id)
          const testRunsData = activeTestRuns.filter(run => run.projectId === projectData.id || run.project_id === projectData.id)
          
          team.projects.push({
            id: projectData.id,
            title: projectData.name || projectData.title || t('dashboard.untitledProject'),
            name: projectData.name || projectData.title || t('dashboard.untitledProject'), // Keep both for compatibility
            description: projectData.description || t('dashboard.noDescription'),
            status: projectData.status || STATUS_VALUES.PROJECT.ACTIVE,
            testCases: testCasesData.length,
            bugs: bugsData.length,
            features: featuresData.length,
            testRuns: testRunsData.length,
            testCasesData: testCasesData, // Store actual test cases data for searching
            bugsData: bugsData, // Store actual bugs data for searching
            featuresData: featuresData, // Store actual features data for searching
            testRunsData: testRunsData, // Store actual test runs data for searching
            createdBy: creatorName,
            createdAt: new Date(projectData.created_at) || new Date(),
            updatedAt: new Date(projectData.updated_at) || new Date()
          })
        }
        
        teamsData.push(team)
      }
    } else {
      // Create a default team with unassigned projects (only visible ones)
        const unassignedProjects = visibleProjects ? visibleProjects.filter(project => !project.teamId) : []
      
      if (unassignedProjects.length > 0) {
        const defaultTeam = {
          id: 'default-team',
          name: t('dashboard.unassignedProjects'),
          projects: []
        }
        
        for (const projectData of unassignedProjects) {
          // Get user name from map
          let creatorName = t('dashboard.unknown')
          const creatorId = projectData.createdBy || projectData.created_by
          if (creatorId) {
            const user = userMap.get(creatorId)
            if (user) {
              creatorName = user.name || t('dashboard.unknown')
            }
          }
          
          // Filter data by project ID (no API calls, just array filtering)
          const testCasesData = activeTestCases.filter(tc => tc.projectId === projectData.id || tc.project_id === projectData.id)
          const bugsData = activeBugs.filter(bug => bug.projectId === projectData.id || bug.project_id === projectData.id)
          const featuresData = activeFeatures.filter(feature => feature.projectId === projectData.id || feature.project_id === projectData.id)
          const testRunsData = activeTestRuns.filter(run => run.projectId === projectData.id || run.project_id === projectData.id)
          
          defaultTeam.projects.push({
            id: projectData.id,
            title: projectData.name || projectData.title || t('dashboard.untitledProject'),
            name: projectData.name || projectData.title || t('dashboard.untitledProject'), // Keep both for compatibility
            description: projectData.description || t('dashboard.noDescription'),
            status: projectData.status || STATUS_VALUES.PROJECT.ACTIVE,
            testCases: testCasesData.length,
            bugs: bugsData.length,
            features: featuresData.length,
            testRuns: testRunsData.length,
            testCasesData: testCasesData,
            bugsData: bugsData,
            featuresData: featuresData,
            testRunsData: testRunsData,
            createdBy: creatorName,
            createdAt: new Date(projectData.created_at) || new Date(),
            updatedAt: new Date(projectData.updated_at) || new Date()
          })
        }
        
        teamsData.push(defaultTeam)
      }
    }
    
    // If no teams or projects exist, show empty state
    if (teamsData.length === 0 && allProjects.length === 0) {
      teams.value = []
      statsLoading.value = false
      return
    }
    
    teams.value = teamsData
    // Data is now loaded, set statsLoading to false
    statsLoading.value = false
    }
    
    // Start loading detailed data in background (non-blocking)
    loadDetailedData().catch(error => {
      console.error('Error loading detailed dashboard data:', error)
      statsLoading.value = false
    })
    
  } catch (error) {
    console.error('Error loading dashboard data:', error)
    toast.error(t('dashboard.errorLoadingData'))
    loading.value = false
  }
}

const refreshData = () => {
  loadData(true) // Force refresh to bypass cache
}

const goToReports = () => {
  router.push({ 
    path: '/reports',
    query: { tab: 'overview' }
  })
}

const goToAssignedToMe = () => {
  router.push('/assigned-to-me')
}

const goToCrossTeamSanity = () => {
  router.push('/cross-team-sanity')
}

const goToTeamsPage = () => {
  router.push('/teams')
}

const goToTeamPage = (teamId) => {
  router.push(`/teams/${teamId}`)
}

const reportBug = () => {
  if (availableProjects.value.length === 0) {
    toast.error('No projects available. Please create a project first.')
    return
  }
  
  if (availableProjects.value.length === 1) {
    // If only one project, go directly to create bug for that project
    router.push(`/projects/${availableProjects.value[0].id}/bugs/create`)
  } else {
    // Show project selector modal
    showProjectSelector.value = true
  }
}

const handleProjectSelected = (project) => {
  showProjectSelector.value = false
  router.push(`/projects/${project.id}/bugs/create`)
}

const handleSearch = (query, filter) => {
  // Update refs from SearchBar component
  if (filter !== undefined) {
    searchType.value = filter
  }
  
  if (!query || !query.trim()) {
    searchResults.value = []
    return
  }
  
  const searchTerm = query.toLowerCase().trim()
  const results = []
  
  // Search through teams and their projects
  teams.value.forEach(team => {
    // Search team name
    if (team.name.toLowerCase().includes(searchTerm)) {
      const projectCount = team.projects?.length || 0
      results.push({
        id: team.id,
        type: 'team',
        title: team.name,
        description: tPlural('dashboard.teamWithProjects', 'dashboard.teamWithProjectsPlural', projectCount),
        parentInfo: t('dashboard.teamLabel'),
        status: null,
        route: '/team-management'
      })
    }
    
    // Search through projects
    team.projects?.forEach(project => {
      // Check if project itself matches
      const projectMatches = projectMatchesSearch(project, searchTerm)
      
      if (projectMatches) {
        results.push({
          id: project.id,
          type: 'project',
          title: project.title,
          description: project.description,
          parentInfo: `${t('dashboard.teamLabel')} ${team.name}`,
          status: project.status,
          route: `/projects/${project.id}`
        })
      }
      
      // Always search through test cases, bugs, features, and test runs regardless of project match
      // Search through test cases
      project.testCasesData?.forEach(testCase => {
        if (testCaseMatchesSearch(testCase, searchTerm)) {
          results.push({
            id: testCase.id,
            type: 'testcase',
            title: testCase.name || testCase.title,
            description: testCase.description,
            parentInfo: `${t('dashboard.projectLabel')} ${project.title}`,
            status: testCase.status,
            route: `/projects/${project.id}/test-cases/${testCase.id}`
          })
        }
      })
      
      // Search through bugs
      project.bugsData?.forEach(bug => {
        if (bugMatchesSearch(bug, searchTerm)) {
          results.push({
            id: bug.id,
            type: 'bug',
            title: bug.title,
            description: bug.description,
            parentInfo: `${t('dashboard.projectLabel')} ${project.title}`,
            status: bug.status,
            route: `/projects/${project.id}/bugs/${bug.id}`
          })
        }
      })
      
      // Search through features
      project.featuresData?.forEach(feature => {
        if (featureMatchesSearch(feature, searchTerm)) {
          results.push({
            id: feature.id,
            type: 'feature',
            title: feature.title || feature.name,
            description: feature.description,
            parentInfo: `${t('dashboard.projectLabel')} ${project.title}`,
            status: feature.status,
            route: `/projects/${project.id}/features/${feature.id}`
          })
        }
      })
      
      // Search through test runs
      project.testRunsData?.forEach(testRun => {
        if (testRunMatchesSearch(testRun, searchTerm)) {
          results.push({
            id: testRun.id,
            type: 'testrun',
            title: testRun.name || testRun.title,
            description: testRun.description,
            parentInfo: `${t('dashboard.projectLabel')} ${project.title}`,
            status: testRun.status,
            route: `/projects/${project.id}/test-runs/${testRun.id}`
          })
        }
      })
    })
  })
  
  // Filter by search type if one is selected
  if (searchType.value) {
    searchResults.value = results.filter(result => result.type === searchType.value)
  } else {
    searchResults.value = results
  }
}

const handleFilterChange = (filterValue) => {
  searchType.value = filterValue
  if (searchQuery.value.trim()) {
    handleSearch(searchQuery.value, filterValue)
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = []
}

// Helper functions for individual item searches
const testCaseMatchesSearch = (testCase, query) => {
  return (
    testCase.name?.toLowerCase().includes(query) ||
    testCase.title?.toLowerCase().includes(query) ||
    testCase.description?.toLowerCase().includes(query) ||
    testCase.status?.toLowerCase().includes(query) ||
    testCase.priority?.toLowerCase().includes(query) ||
    testCase.type?.toLowerCase().includes(query)
  )
}

const bugMatchesSearch = (bug, query) => {
  return (
    bug.title?.toLowerCase().includes(query) ||
    bug.description?.toLowerCase().includes(query) ||
    bug.status?.toLowerCase().includes(query) ||
    bug.priority?.toLowerCase().includes(query) ||
    bug.severity?.toLowerCase().includes(query) ||
    bug.type?.toLowerCase().includes(query)
  )
}

const featureMatchesSearch = (feature, query) => {
  return (
    feature.title?.toLowerCase().includes(query) ||
    feature.name?.toLowerCase().includes(query) ||
    feature.description?.toLowerCase().includes(query) ||
    feature.status?.toLowerCase().includes(query) ||
    feature.priority?.toLowerCase().includes(query) ||
    feature.type?.toLowerCase().includes(query)
  )
}

const testRunMatchesSearch = (testRun, query) => {
  return (
    testRun.name?.toLowerCase().includes(query) ||
    testRun.title?.toLowerCase().includes(query) ||
    testRun.description?.toLowerCase().includes(query) ||
    testRun.status?.toLowerCase().includes(query) ||
    testRun.environment?.toLowerCase().includes(query)
  )
}

// Navigation and UI functions
const navigateToResult = async (result) => {
  searchQuery.value = ''
  searchResults.value = []
  
  // If result is a team, scroll to it and select it in the Teams & Projects section
  if (result.type === 'team') {
    selectedTeamId.value = result.id
    // The watch on selectedTeamId will handle scrolling
  } else {
    router.push(result.route)
  }
}

// Removed: getTypeBadgeClass and getTypeLabel are now in badgeHelpers.js

// Helper method to check if a project matches the search query
const projectMatchesSearch = (project, query) => {
  // Convert numeric values to strings for comparison
  const testCasesStr = (project.testCases || 0).toString()
  const bugsStr = (project.bugs || 0).toString()
  
  // Check if query is a number (for test cases/bugs search)
  const isNumericQuery = query.match(/^\d+$/)
  
  // Search through project basic info
  const projectMatches = (
    project.title.toLowerCase().includes(query) ||
    project.description.toLowerCase().includes(query) ||
    project.status.toLowerCase().includes(query) ||
    // For numeric queries, check exact matches first
    (isNumericQuery && (testCasesStr === query || bugsStr === query)) ||
    // For text queries, check if numbers contain the query
    (!isNumericQuery && (testCasesStr.includes(query) || bugsStr.includes(query)))
  )
  
  // Search through test cases data
  const testCasesMatch = project.testCasesData?.some(testCase => {
    return (
      testCase.name?.toLowerCase().includes(query) ||
      testCase.title?.toLowerCase().includes(query) ||
      testCase.description?.toLowerCase().includes(query) ||
      testCase.status?.toLowerCase().includes(query) ||
      testCase.priority?.toLowerCase().includes(query) ||
      testCase.type?.toLowerCase().includes(query)
    )
  }) || false
  
  // Search through bugs data
  const bugsMatch = project.bugsData?.some(bug => {
    return (
      bug.title?.toLowerCase().includes(query) ||
      bug.description?.toLowerCase().includes(query) ||
      bug.status?.toLowerCase().includes(query) ||
      bug.priority?.toLowerCase().includes(query) ||
      bug.severity?.toLowerCase().includes(query) ||
      bug.type?.toLowerCase().includes(query)
    )
  }) || false
  
  // Search through features data
  const featuresMatch = project.featuresData?.some(feature => {
    return (
      feature.title?.toLowerCase().includes(query) ||
      feature.name?.toLowerCase().includes(query) ||
      feature.description?.toLowerCase().includes(query) ||
      feature.status?.toLowerCase().includes(query) ||
      feature.priority?.toLowerCase().includes(query) ||
      feature.type?.toLowerCase().includes(query)
    )
  }) || false
  
  // Search through test runs data
  const testRunsMatch = project.testRunsData?.some(testRun => {
    return (
      testRun.name?.toLowerCase().includes(query) ||
      testRun.title?.toLowerCase().includes(query) ||
      testRun.description?.toLowerCase().includes(query) ||
      testRun.status?.toLowerCase().includes(query) ||
      testRun.environment?.toLowerCase().includes(query)
    )
  }) || false
  
  return projectMatches || testCasesMatch || bugsMatch || featuresMatch || testRunsMatch
}

// Lifecycle
onMounted(async () => {
  // Only load data if we're on the dashboard route
  // This prevents unnecessary API calls when navigating away
  if (route.name === 'Dashboard') {
  await loadData()
  
  // Automatically select the first team (with most projects) when dashboard loads
  if (filteredTeams.value.length > 0) {
    const teamsSorted = [...filteredTeams.value].sort((a, b) => {
      const aProjects = a.projects?.length || 0
      const bProjects = b.projects?.length || 0
      return bProjects - aProjects
    })
    selectedTeamId.value = teamsSorted[0]?.id || null
    }
  }
  
  // Add click outside handler for team actions dropdown
  document.addEventListener('click', handleClickOutside)
  
  // Add window resize listener for responsive behavior
  const handleResize = () => {
    isMobile.value = window.innerWidth < 768
  }
  window.addEventListener('resize', handleResize)
  
  // Store the handler for cleanup
  window.__dashboardResizeHandler = handleResize
})

// Watch route to prevent loading when not on dashboard
watch(() => route.name, (routeName) => {
  // Only load data if we're navigating TO the dashboard
  // Don't load if we're navigating away from it
  if (routeName === 'Dashboard' && teams.value.length === 0) {
    loadData()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  
  // Clear debounce timer
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  
  // Remove window resize listener
  if (window.__dashboardResizeHandler) {
    window.removeEventListener('resize', window.__dashboardResizeHandler)
    delete window.__dashboardResizeHandler
  }
})

// Click outside handler
const handleClickOutside = (event) => {
  if (!event.target.closest('[ref="searchDropdownRef"]') && !event.target.closest('input[placeholder*="Search"]')) {
    // Handle search dropdown close if needed
  }
}
</script> 